import { useState, type FormEvent } from 'react';
import { Building2, User } from 'lucide-react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { TagsInput } from '@/components/tags-input';
import { Textarea } from '@/components/textarea';
import { cn } from '@/lib/cn';
import { useFormError } from '@/lib/form-error';
import { US_STATES } from '@/lib/us-states';
import type { ClientData } from '@/types/api';

type ClientInput = Partial<ClientData> & { create_stripe_customer?: boolean };

type Props = {
  initial?: Partial<ClientData>;
  onSubmit: (input: ClientInput) => Promise<unknown>;
  submitLabel: string;
  /** Show the opt-in to create a Stripe customer on save (new-client flow only). */
  showStripeOption?: boolean;
};

export function ClientForm({ initial = {}, onSubmit, submitLabel, showStripeOption = false }: Props) {
  const [form, setForm] = useState<ClientInput>({
    name: initial.name ?? '',
    kind: initial.kind ?? 'company',
    legal_name: initial.legal_name ?? '',
    website: initial.website ?? '',
    primary_email: initial.primary_email ?? '',
    primary_phone: initial.primary_phone ?? '',
    address_line1: initial.address_line1 ?? '',
    city: initial.city ?? '',
    state: initial.state ?? '',
    postal_code: initial.postal_code ?? '',
    default_hourly_rate_cents: initial.default_hourly_rate_cents ?? null,
    notes: initial.notes ?? '',
  });
  const isIndividual = form.kind === 'individual';
  const [tags, setTags] = useState<string[]>(initial.tags ?? []);
  const [createStripeCustomer, setCreateStripeCustomer] = useState(false);
  const [pending, setPending] = useState(false);
  const { error, wrap } = useFormError();

  function update<K extends keyof ClientInput>(key: K, value: ClientInput[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    await wrap(() =>
      onSubmit({
        ...form,
        tags,
        ...(showStripeOption ? { create_stripe_customer: createStripeCustomer } : {}),
      }),
    );
    setPending(false);
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <KindToggle
        value={form.kind ?? 'company'}
        onChange={(kind) => {
          if (kind === 'individual') {
            // Stash company-only fields so we don't ship them blank.
            setForm((prev) => ({ ...prev, kind, legal_name: '', website: '' }));
          } else {
            setForm((prev) => ({ ...prev, kind }));
          }
        }}
      />
      <div className="grid grid-cols-2 gap-4">
        <Field label={isIndividual ? 'Full name' : 'Name'} className={isIndividual ? 'col-span-2' : undefined}>
          <Input
            required
            value={form.name ?? ''}
            onChange={(e) => update('name', e.target.value)}
            placeholder={isIndividual ? 'Jane Smith' : 'Acme Inc.'}
          />
        </Field>
        {isIndividual ? null : (
          <>
            <Field label="Legal name">
              <Input
                value={form.legal_name ?? ''}
                onChange={(e) => update('legal_name', e.target.value)}
              />
            </Field>
            <Field label="Website">
              <Input
                type="url"
                placeholder="https://"
                value={form.website ?? ''}
                onChange={(e) => update('website', e.target.value)}
              />
            </Field>
          </>
        )}
        <Field label="Primary email">
          <Input
            type="email"
            value={form.primary_email ?? ''}
            onChange={(e) => update('primary_email', e.target.value)}
          />
        </Field>
        <Field label="Primary phone">
          <Input
            value={form.primary_phone ?? ''}
            onChange={(e) => update('primary_phone', e.target.value)}
          />
        </Field>
        <Field label="Address" className="col-span-2">
          <Input
            value={form.address_line1 ?? ''}
            onChange={(e) => update('address_line1', e.target.value)}
          />
        </Field>
        <Field label="City">
          <Input value={form.city ?? ''} onChange={(e) => update('city', e.target.value)} />
        </Field>
        <Field label="State">
          <Select
            value={form.state ?? ''}
            onChange={(e) => update('state', e.target.value)}
          >
            <option value="">—</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.code} — {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Postal code">
          <Input
            value={form.postal_code ?? ''}
            onChange={(e) => update('postal_code', e.target.value)}
          />
        </Field>
        <Field label="Default hourly rate ($)">
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="150.00"
            value={
              form.default_hourly_rate_cents != null
                ? (form.default_hourly_rate_cents / 100).toString()
                : ''
            }
            onChange={(e) => {
              const v = e.target.value;
              update(
                'default_hourly_rate_cents',
                v === '' ? null : Math.round(parseFloat(v) * 100),
              );
            }}
          />
        </Field>
        <Field label="Tags">
          <TagsInput value={tags} onChange={setTags} />
        </Field>
      </div>
      <Field label="Notes">
        <Textarea
          value={form.notes ?? ''}
          onChange={(e) => update('notes', e.target.value)}
        />
      </Field>
      {showStripeOption ? (
        <label className="flex items-start gap-2.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3">
          <input
            type="checkbox"
            checked={createStripeCustomer}
            onChange={(e) => setCreateStripeCustomer(e.target.checked)}
            className="mt-0.5 size-4"
          />
          <span className="text-sm">
            <span className="font-medium text-[var(--color-foreground)]">Set up Stripe billing</span>
            <span className="mt-0.5 block text-xs text-[var(--color-foreground-muted)]">
              Creates a Stripe customer (or links an existing one matching this email) so you can add
              cards and bill this client. You can also do this later from the Billing tab.
            </span>
          </span>
        </label>
      ) : null}
      {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block space-y-1.5 ${className ?? ''}`}>
      <span className="text-xs font-medium text-[var(--color-foreground-muted)]">{label}</span>
      {children}
    </label>
  );
}

function KindToggle({
  value,
  onChange,
}: {
  value: string;
  onChange: (kind: 'company' | 'individual') => void;
}) {
  const options: Array<{ key: 'company' | 'individual'; label: string; icon: typeof Building2 }> = [
    { key: 'company', label: 'Company', icon: Building2 },
    { key: 'individual', label: 'Individual', icon: User },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Client kind"
      className="inline-flex rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-0.5"
    >
      {options.map((opt) => {
        const active = value === opt.key;
        const Icon = opt.icon;
        return (
          <button
            key={opt.key}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.key)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors',
              active
                ? 'bg-[var(--color-surface)] text-[var(--color-foreground)] shadow-sm'
                : 'text-[var(--color-foreground-muted)] hover:text-[var(--color-foreground)]',
            )}
          >
            <Icon className="size-3.5" />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
